import { Fragment, type ReactNode } from 'react';
import styled from 'styled-components';

import { zIndexMap } from '../../../../shared/config';
import { rubikTypographyStyles } from '../../../../shared/ui';
import {
  useRaffleCountdown,
  type RaffleCountdownTarget,
} from '../model/use-raffle-countdown';

export type RaffleCountdownLabels = {
  days: string;
  hours: string;
  minutes: string;
  seconds: string;
};

export type RaffleCountdownCardProps = {
  caption?: ReactNode;
  className?: string;
  expiredText?: ReactNode;
  labels?: RaffleCountdownLabels;
  targetAt: RaffleCountdownTarget;
  title: ReactNode;
};

const DEFAULT_UNIT_LABELS: RaffleCountdownLabels = {
  days: 'days',
  hours: 'hours',
  minutes: 'minutes',
  seconds: 'seconds',
};

const Card = styled.section.attrs({
  'data-ui': 'raffle-countdown-card',
})`
  ${rubikTypographyStyles};
  position: relative;
  z-index: ${zIndexMap.raffle.countdown};
  display: flex;
  flex-direction: column;
  align-items: center;
  box-sizing: border-box;
`;

const Title = styled.h2`
  margin: 0;
  text-align: center;
  font-size: ${({ theme }) => theme.scale.px(14)};
  font-weight: 400;
  line-height: 1.1;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const CountdownValue = styled.p`
  margin: ${({ theme }) => `${theme.scale.px(8)} 0 0`};
  text-align: center;
  font-family: Inter, sans-serif;
  font-size: ${({ theme }) => theme.scale.px(24)};
  font-weight: 900;
  line-height: 1.03;
  color: ${({ theme }) => theme.colors.text.primary};
  font-variant-numeric: tabular-nums;
  text-transform: uppercase;
`;

const CountdownSegments = styled.div`
  display: inline-flex;
  align-items: flex-start;
  justify-content: center;
  margin-top: ${({ theme }) => theme.scale.px(4)};
`;

const CountdownSegment = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const CountdownSegmentValue = styled.span`
  font-family: Inter, sans-serif;
  font-size: ${({ theme }) => theme.scale.px(24)};
  font-weight: 900;
  line-height: 1.03;
  color: ${({ theme }) => theme.colors.text.primary};
  font-variant-numeric: tabular-nums;
  text-transform: uppercase;
  text-align: center;
`;

const CountdownSeparator = styled.span`
  margin: 0 ${({ theme }) => theme.scale.px(8)};
  font-family: Inter, sans-serif;
  font-size: ${({ theme }) => theme.scale.px(24)};
  font-weight: 900;
  line-height: 1.03;
  color: ${({ theme }) => theme.colors.text.primary};
  font-variant-numeric: tabular-nums;
`;

const CountdownUnitLabel = styled.span`
  font-family: Inter, sans-serif;
  font-size: ${({ theme }) => theme.scale.px(8)};
  font-weight: 500;
  line-height: 1.03;
  color: rgba(0, 0, 0, 0.5);
  text-transform: uppercase;
  margin-top: ${({ theme }) => theme.scale.px(4)};
`;

const Caption = styled.p`
  margin: ${({ theme }) => `${theme.scale.px(8)} 0 0`};
  text-align: center;
  font-size: ${({ theme }) => theme.scale.px(12)};
  line-height: ${({ theme }) => theme.scale.px(14)};
  color: ${({ theme }) => theme.colors.text.subdued};
`;

export function RaffleCountdownCard({
  caption,
  className,
  expiredText,
  labels,
  targetAt,
  title,
}: RaffleCountdownCardProps) {
  const countdown = useRaffleCountdown(targetAt);
  const resolvedLabels = labels ?? DEFAULT_UNIT_LABELS;
  const valueText =
    countdown.isExpired && expiredText ? expiredText : countdown.countdownText;
  const shouldShowUnitLabels = !(countdown.isExpired && expiredText);
  const countdownSegments = [
    {
      label: resolvedLabels.days,
      value: String(countdown.days).padStart(2, '0'),
    },
    {
      label: resolvedLabels.hours,
      value: String(countdown.hours).padStart(2, '0'),
    },
    {
      label: resolvedLabels.minutes,
      value: String(countdown.minutes).padStart(2, '0'),
    },
    {
      label: resolvedLabels.seconds,
      value: String(countdown.seconds).padStart(2, '0'),
    },
  ];

  return (
    <Card className={className}>
      <Title>{title}</Title>
      {shouldShowUnitLabels ? (
        <CountdownSegments aria-label={countdown.countdownText} role="group">
          {countdownSegments.map((segment, index) => (
            <Fragment key={segment.label}>
              {index > 0 ? <CountdownSeparator>:</CountdownSeparator> : null}
              <CountdownSegment>
                <CountdownSegmentValue>{segment.value}</CountdownSegmentValue>
                <CountdownUnitLabel>{segment.label}</CountdownUnitLabel>
              </CountdownSegment>
            </Fragment>
          ))}
        </CountdownSegments>
      ) : (
        <CountdownValue>{valueText}</CountdownValue>
      )}
      {caption ? <Caption>{caption}</Caption> : null}
    </Card>
  );
}
